"use client";

import type { PlaceResult } from "@ishvaram/ui/hooks";
import { formatPlaceOptionLabel } from "./PlaceAutocomplete.option";

/**
 * Selected-city chip — the 2026 mobile-autocomplete standard
 * (Baymard "Mobile Search Autocomplete"; Smart Interface Design Patterns):
 * an UNMISTAKABLE, one-tap-correctable confirmation that the city is locked.
 *
 * Why it exists: on mobile, a typed birth city with no visible confirmation
 * left seekers unsure whether selection "took" — a documented driver of the
 * 15,124 birthPlace failures. A chip removes that doubt at the source: chip
 * shown === `placeSelected` true. The single "change" affordance is the one
 * obvious correction path (rule 39 — one decision per surface, no competing
 * controls), so a wrong city is one tap from re-search, never a dead end.
 */
export function PlaceChip({
  place,
  onChange,
  changeLabel,
}: {
  place: PlaceResult;
  onChange: () => void;
  changeLabel: string;
}) {
  const { cityName, region } = formatPlaceOptionLabel(place.display);
  return (
    <div
      data-testid="place-chip"
      className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5"
      style={{
        background: "var(--ish-accent-light)",
        border: "1px solid var(--ish-accent-border)",
      }}
    >
      <span className="flex items-center gap-1.5 text-[14px]" style={{ color: "var(--ish-dark)" }}>
        <span aria-hidden style={{ color: "var(--ish-accent)", flexShrink: 0 }}>
          ✓
        </span>
        <span style={{ fontWeight: 600 }}>{cityName}</span>
        {region && (
          <span style={{ color: "var(--ish-text-muted)", fontSize: 12 }}>{`, ${region}`}</span>
        )}
      </span>
      <button
        type="button"
        onClick={onChange}
        className="text-[12px] font-semibold"
        style={{
          background: "none",
          border: "none",
          padding: "2px 4px",
          cursor: "pointer",
          color: "var(--ish-accent, var(--color-accent))",
          textDecoration: "underline",
          flexShrink: 0,
        }}
      >
        {changeLabel}
      </button>
    </div>
  );
}

/**
 * "Did you mean …?" prompt — shown when a typed city resolved as AMBIGUOUS
 * (multiple plausible matches, none confidently the user's). One tappable
 * suggestion to commit (AWS/Baymard "offer the likely correction, don't
 * hard-block"). Distinct from the chip: this is a SUGGESTION, the chip is a
 * CONFIRMATION.
 */
export function PlaceDidYouMean({
  suggestion,
  onAccept,
  prefixLabel,
}: {
  suggestion: PlaceResult;
  onAccept: (place: PlaceResult) => void;
  prefixLabel: string;
}) {
  const { cityName, region } = formatPlaceOptionLabel(suggestion.display);
  return (
    <p className="mt-1.5 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
      {prefixLabel}{" "}
      <button
        type="button"
        data-testid="place-did-you-mean"
        onClick={() => onAccept(suggestion)}
        className="font-semibold"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--ish-accent, var(--color-accent))",
          textDecoration: "underline",
        }}
      >
        {region ? `${cityName}, ${region}` : cityName}
      </button>
      {"?"}
    </p>
  );
}
